import { useNavigate } from 'react-router-dom';
import Breadcrumb from './nav/Breadcrumb';
import EmptyState from './nav/EmptyState';
import PageSkeleton from './nav/PageSkeleton';

import { useCalendarioData } from './calendario/useCalendarioData';
import CalendarHeader from './calendario/CalendarHeader';
import MeseView from './calendario/MeseView';
import AgendaView from './calendario/AgendaView';
import WeekView from './calendario/WeekView';
import MiniMonth from './calendario/MiniMonth';

/**
 * PaginaCalendario — orchestratore della pagina Calendario.
 *
 * Tre viste: Mese / Trimestre / Settimana. Tutta la logica dati/mutation è
 * incapsulata in `useCalendarioData`. Questo componente seleziona il
 * sotto-componente di rendering in base a `view` e passa gli handler.
 *
 * Refactor sessione 52: da 983 righe → ~85 righe. Sotto-componenti in
 * `components/calendario/*`. Hook condivisi/utility in `lib/calendarConstants.js`
 * + `lib/weekFromIsoDate.js`.
 */
export default function PaginaCalendario() {
  const navigate = useNavigate();
  const {
    view, year, month, isoWeek, todayIso,
    data, trimData, weekData, loading,
    prev, next, goToday, setView,
    handleToggleMit, handleToggleAzione, reassignMitMese, load,
  } = useCalendarioData();

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <div className="max-w-7xl mx-auto px-6 pt-2">
        <Breadcrumb
          backTo="/"
          backLabel="Dashboard"
          items={[{ label: 'Dashboard', to: '/' }, { label: 'Calendario' }]}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-20">
        <CalendarHeader
          view={view} year={year} month={month} isoWeek={isoWeek}
          onPrev={prev} onNext={next} onToday={goToday} onViewChange={setView}
        />

        {loading && (
          <div data-testid="cal-loading">
            {view === 'settimana'
              ? <PageSkeleton variant="list" inline sections={1} rowsPerSection={7} testid="cal-skeleton-settimana" />
              : <PageSkeleton variant="calendar" inline testid="cal-skeleton-mese" />}
          </div>
        )}

        {!loading && view === 'mese' && data && (
          <>
            {/* Desktop: grid mensile 8 colonne. Mobile: lista agenda. */}
            <div className="hidden md:block">
              <MeseView
                data={data}
                todayIso={todayIso}
                onToggleMit={handleToggleMit}
                onToggleAzione={handleToggleAzione}
                onReassignMit={reassignMitMese}
              />
            </div>
            <div className="md:hidden">
              <AgendaView
                data={data}
                todayIso={todayIso}
                onToggleMit={handleToggleMit}
                onToggleAzione={handleToggleAzione}
              />
            </div>
            {(data.totali?.mit || 0) === 0 && (data.totali?.scadenze || 0) === 0 && (data.totali?.azioni || 0) === 0 && (
              <div className="mt-8">
                <EmptyState
                  title="Mese senza attività"
                  desc="Non ci sono MIT né scadenze pianificate in questo mese. Pianificalo col coach per partire con un quadro chiaro."
                  actionLabel="Pianifica il mese"
                  actionTo="/mese"
                  secondary={{ label: 'Aggiungi MIT (⇧N)', onClick: () => window.dispatchEvent(new CustomEvent('qp:quick-add-open')) }}
                  testid="cal-mese-empty"
                />
              </div>
            )}
          </>
        )}

        {!loading && view === 'trimestre' && trimData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="cal-trimestre">
            {trimData.map((m) => (
              <MiniMonth
                key={`${m.year}-${m.month}`}
                year={m.year} month={m.month} data={m.data}
                todayIso={todayIso}
                onPickDay={(d) => navigate(`/periodo/giorno/${d}`)}
              />
            ))}
          </div>
        )}

        {!loading && view === 'settimana' && weekData && (
          <WeekView
            isoWeek={isoWeek}
            data={weekData}
            todayIso={todayIso}
            onToggleMit={handleToggleMit}
            onToggleAzione={handleToggleAzione}
            onReload={load}
          />
        )}
      </div>
    </div>
  );
}
